import React, { useState, useCallback, useMemo, Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import { flatten, isEmpty, isArray } from 'underscore';
import classnames from 'classnames';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import {
  useSiteConfig,
  useInstrumentationServiceContext,
  useIsSmartPageM365StockImageGalleryEnabled,
} from '@bingads-webui-campaign-react/smart-page-context';
import {
  useUploadService,
  useFacebookPhotos,
  SmartPivotImageEditor,
  AddImagesPlaceholder,
  EditImagesView,
  ImageOptionsKey,
} from '@bingads-webui-campaign-react/smart-component-image';
import {
  getISOTimeStringWithTimeZone,
  getUploadUrlFromImage,
  ImageSource,
  getUploadDetailFromImage,
  DATA_SOURCE_KEY_MAPPING,
  getDataSourceByKey,
  truncateStringEndWithEmoji,
} from '@bingads-webui-campaign-react/smart-component-util';
import {
  SmartTextEditor,
} from '@bingads-webui-campaign-react/smart-component-text';

import { Dialog, DialogType, DialogFooter } from '@fluentui/react/lib/Dialog';
import { PrimaryButton, DefaultButton } from '@fluentui/react/lib/Button';
import { Label } from '@fluentui/react/lib/Label';

import { CARD_DIALOG_MODE, CARD_SOURCE } from './constants';

const getStyles = () => ({
  cardEditor: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingLeft: 24,
  },
  cardEditorSidePanel: {
    width: 448,
  },
  headerStyle: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '27px',
    color: '#323130',
    margin: '23px 0px',
    border: '0px',
    padding: '0px',
  },
  cardTextArea: {
    marginBottom: 60,
    width: 400,
    height: 200,
  },
  smartTextEditor: {
    '& .ms-TextField-errorMessage': {
      justifyContent: 'flex-end',
    },
  },
  warningIcon: {
    fontSize: '16px',
    lineHeight: '19px',
    marginRight: '6px',
    color: '#F1AF01',
  },
  cardEditorPreview: {
    width: 504,
    height: 660,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    background: '#E8E8E8',
    '& label': {
      position: 'absolute',
      top: 24,
      left: 20,
    },
    '& .card': {
      background: '#fff',
    },
  },
  dialogContentStyle: {
    '& .ms-Dialog-title': {
      minHeight: 0,
      padding: 0,
    },
    '& .ms-Dialog-inner': {
      padding: 0,
    },
    '& .ms-Dialog-actionsRight': {
      marginRight: 32,
      paddingBottom: 24,
      display: 'flex',
      justifyContent: 'end',
      '& .ms-Dialog-action': {
        display: 'flex',
        alignItems: 'center',
      },
    },
  },
  imageError: {
    color: '#A80000',
    fontSize: 14,
  },
});

const UnStyledCardDialog = ({
  ItemComponent,
  characterSoftLimit,
  characterHardLimit,
  i18n,
  classes,
  onAdd,
  onCancel,
  targetCardIndex,
  preselectedCards,
  gotoSettings,
}) => {
  const { siteConfig } = useSiteConfig();
  const logger = useInstrumentationServiceContext();
  const smartPageId = siteConfig.id;
  const selectedCards = useMemo(() => cloneDeep(preselectedCards), [preselectedCards]);

  const businessCategories =
    getDataSourceByKey(siteConfig, DATA_SOURCE_KEY_MAPPING.BusinessCategories);

  let currentCard = null;
  let isCreate = true;

  if (!isEmpty(selectedCards) &&
    targetCardIndex >= 0 &&
    targetCardIndex < selectedCards.length) {
    currentCard = selectedCards[targetCardIndex];
    isCreate = false;
  }

  const createdTime = currentCard ? currentCard.createdTime : '';

  const [isLoading, setIsLoading] = useState(false);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [selectedImageType, setSelectedImageType] = useState(ImageSource.StockImage);
  const [cardMessage, setCardMessage] = useState(currentCard && currentCard.message ? currentCard.message : '');
  const [cardPhotoUrl, setCardPhotoUrl]
    = useState(currentCard ? currentCard.photo : undefined);
  const [imageError, setImageError] = useState('');
  // Need this ref, otherwise will make useFacebookPhotos rerender infinitely.
  const emptyImageListRef = useRef([]);

  const { uploadImageAsync, bulkUploadImagesByUrlsAsync } = useUploadService();

  const isSmartPageM365StockImageGalleryEnabled = useIsSmartPageM365StockImageGalleryEnabled();
  const [shouldStartFetchFbPhotos, setShouldStartFetchFbPhotos]
    = useState(!isSmartPageM365StockImageGalleryEnabled);
  const {
    isFbSupported,
    fbPhotos,
    isInitializing,
  } = useFacebookPhotos(emptyImageListRef.current, shouldStartFetchFbPhotos);

  const onAddClicked = () => {
    const newCard = currentCard || {};
    const date = new Date();
    const timeString = getISOTimeStringWithTimeZone(date);

    newCard.message = cardMessage.trim();

    if (isCreate) {
      newCard.createdTime = timeString;
      newCard.source = CARD_SOURCE.SMARTPAGE;
      newCard.id = `${smartPageId}_${date.getTime()}`;
      selectedCards.unshift(newCard);
    } else {
      newCard.updatedTime = timeString;
    }

    if ((isCreate && !cardPhotoUrl) ||
      (currentCard && currentCard.photo === cardPhotoUrl)) {
      if (isCreate) {
        logger.logTrace(`Create an update with text : ${JSON.stringify(newCard)}`);
      } else {
        logger.logTrace(`Update an update with text : ${JSON.stringify(newCard)}`);
      }

      onAdd({ newSelectedUpdates: selectedCards });

      return;
    }

    const imageIdsToRemove = [];

    if (currentCard && currentCard.photo && !cardPhotoUrl) {
      logger.logTrace(`Remove image of an update: ${JSON.stringify(newCard)}`);

      imageIdsToRemove.push(currentCard.imageId);

      delete newCard.photo;
      delete newCard.fbPhotoSrc;
      delete newCard.externalSrc;
      delete newCard.imageId;

      onAdd({ newSelectedUpdates: selectedCards, imageIdsToRemove });

      return;
    }

    const uploadImagePromise = selectedImageType === ImageSource.Local ?
      uploadImageAsync(cardPhotoUrl) : bulkUploadImagesByUrlsAsync([cardPhotoUrl]);

    uploadImagePromise
      .finally((() => {
        setIsLoading(true);
        return () => {
          setIsLoading(false);
        };
      })())
      .then((result) => {
        const imageToAdd = isArray(result) ? result[0] : result;

        if (newCard.imageId) {
          imageIdsToRemove.push(newCard.imageId);
        }

        const { IdString, ImageUrl } = imageToAdd;

        if (selectedImageType === ImageSource.StockImage) {
          newCard.externalSrc = cardPhotoUrl;
        }

        newCard.imageId = IdString;
        newCard.photo = ImageUrl;

        if (isCreate) {
          logger.logTrace(`Create an update with image : ${JSON.stringify(newCard)}`);
        } else {
          logger.logTrace(`Update an update with image : ${JSON.stringify(newCard)}, remove imageId: ${flatten(imageIdsToRemove).toString()}`);
        }

        onAdd({
          newSelectedUpdates: selectedCards,
          imagesToAdd: [imageToAdd],
          imageIdsToRemove,
        });
      })
      .catch((err) => {
        logger.logTrace(`SmartPage|CreateDialog|AddImageError, ${JSON.stringify(err)}`);
        setImageError(i18n.getString(_TL_('Failed to add the image. Please try again')));
      });
  };

  const dialogContentProps = useMemo(() => ({
    className: classes.dialogContentStyle,
    type: DialogType.normal,
  }), [classes]);

  const cardMessageError = useMemo(() => (
    <div>
      <span className={classnames(classes.warningIcon, 'iconba', 'iconba-IncidentTriangle')} />
      {i18n.getString(_TL_('You\'ve exceeded the maximum characters {{num}} allowed on this post.'), { num: characterSoftLimit })}
    </div>
  ), [i18n, classes, characterSoftLimit]);

  const [errorMessage, setErrorMessage] = useState(cardMessage.length > characterSoftLimit ? cardMessageError : '');

  const onTextAreaChange = useCallback(({ value: newValue }) => {
    if (newValue.length > characterSoftLimit) {
      setErrorMessage(cardMessageError);
    } else {
      setErrorMessage('');
    }

    setCardMessage(newValue.trim() ? newValue : '');
  }, [cardMessageError, characterSoftLimit]);

  const isAddBtnDisabled = useCallback(
    () => ((isEmpty(cardMessage) && isEmpty(cardPhotoUrl)) ||
      (!isCreate && currentCard.message === cardMessage.trim() &&
        currentCard.photo === cardPhotoUrl) ||
      imageError),
    [cardMessage, cardPhotoUrl, currentCard, isCreate, imageError]
  );

  const onImageSelectionChanged = useCallback((selectionList) => {
    if (isArray(selectionList) && !isEmpty(selectionList)) {
      const selection = selectionList[0];
      const url = getUploadUrlFromImage(selection) || selection.imageData;

      const { source } = getUploadDetailFromImage(selection);
      setSelectedImageType(source);

      setCardPhotoUrl(url);
      setImageError('');
    }
  }, []);

  const onPivotLinkClicked = useCallback((key) => {
    if (key === ImageOptionsKey.FACEBOOKIMPORT) {
      setShouldStartFetchFbPhotos(true);
    }
  }, []);

  const serializeValue = useCallback(val =>
    truncateStringEndWithEmoji(val, characterHardLimit), [characterHardLimit]);

  const onDeleteImage = useCallback(() => {
    setCardPhotoUrl(undefined);
    setImageError('');
  }, []);

  return (
    <Dialog
      hidden={false}
      onDismiss={onCancel}
      dialogContentProps={dialogContentProps}
      minWidth={1000}
      modalProps={{ isBlocking: true }}
    >
      <UiBlocker
        blocking={isLoading}
        loadingText={isCreate ? i18n.getString(_TL_('Adding')) : i18n.getString(_TL_('Updating'))}
      >
        <div className={classes.cardEditor}>
          <div className={classes.cardEditorSidePanel}>
            {showImageEditor ?
              <div id="customized-card-image-editor-container">
                <DefaultButton
                  id="back-to-editor-button"
                  className={classes.headerStyle}
                  onClick={() => setShowImageEditor(false)}
                  iconProps={{ iconName: 'Back' }}
                  text={i18n.getString(_TL_('Back'))}
                />
                <SmartPivotImageEditor
                  className={classnames('customized-card-image-editor')}
                  i18n={i18n}
                  onSelectionChanged={onImageSelectionChanged}
                  gotoSettings={gotoSettings}
                  isLoadingFbPhotos={isInitializing}
                  isFbSupported={isFbSupported}
                  isMultiple={false}
                  itemsPerPage={4}
                  defaultKeywords={businessCategories}
                  fbPhotos={fbPhotos}
                  onPivotLinkClicked={onPivotLinkClicked}
                />
              </div> :
              <Fragment>
                <div className={classnames('card-dialog-title', classes.headerStyle)}>
                  {isCreate ? i18n.getString(_TL_('Create your own post')) : i18n.getString(_TL_('Edit your own post'))}
                </div>
                <div className={classes.cardTextArea}>
                  <SmartTextEditor
                    id="customized-card-message-input"
                    className={classes.smartTextEditor}
                    label={i18n.getString(_TL_('What do you want your customers to know?'))}
                    value={cardMessage}
                    onChange={onTextAreaChange}
                    errorMessage={errorMessage}
                    multiline
                    resizable={false}
                    rows={8}
                    maxCharCount={200}
                    serializeValue={serializeValue}
                  />
                </div>
                <div id="customized-card-selected-image">
                  <Label>{i18n.getString(_TL_('Add image to your post'))}</Label>
                  {
                    cardPhotoUrl ?
                      <EditImagesView
                        items={[{ externalSrc: cardPhotoUrl }]}
                        i18n={i18n}
                        onDelete={onDeleteImage}
                        onReplace={() => setShowImageEditor(true)}
                        deleteOnly={false}
                      /> :
                      <AddImagesPlaceholder
                        i18n={i18n}
                        onClick={() => setShowImageEditor(true)}
                        text={i18n.getString(_TL_('Add photo'))}
                      />
                  }
                </div>
              </Fragment>
            }
          </div>
          <div id="customized-card-preview" className={classes.cardEditorPreview}>
            <ItemComponent
              i18n={i18n}
              mode={CARD_DIALOG_MODE.VIEW_MODE}
              item={{
                photo: cardPhotoUrl,
                message: cardMessage,
                createdTime: createdTime || getISOTimeStringWithTimeZone(new Date()),
              }}
            />
          </div>
        </div>

        <DialogFooter>
          {imageError &&
            <div role="alert" className={classnames(classes.imageError, 'card-image-error-message')}>
              {imageError}
            </div>
          }
          <PrimaryButton disabled={isAddBtnDisabled()} onClick={onAddClicked} text={isCreate ? i18n.getString(_TL_('Add post')) : i18n.getString(_TL_('Update'))} />
          <DefaultButton onClick={onCancel} text={i18n.getString(_TL_('Cancel'))} />
        </DialogFooter>
      </UiBlocker>
    </Dialog>
  );
};

UnStyledCardDialog.propTypes = {
  ItemComponent: PropTypes.func.isRequired,
  characterSoftLimit: PropTypes.number.isRequired,
  characterHardLimit: PropTypes.number.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
  }).isRequired,
  onAdd: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  targetCardIndex: PropTypes.number,
  preselectedCards: PropTypes.arrayOf(PropTypes.any).isRequired,
  gotoSettings: PropTypes.func.isRequired,
};

UnStyledCardDialog.defaultProps = {
  targetCardIndex: -1,
};

export const CardDialog = withDefaultStyles(
  UnStyledCardDialog,
  getStyles
);
